/**
 * Focus hand-off between the global search chrome and the Library pane.
 *
 * The workspace layout registers a handler while mounted; Cmd-K and the
 * top-bar magnifying glass call `requestLibrarySearchFocus` first and fall
 * back to the GlobalSearchModal when no workspace is showing. This keeps the
 * modal for non-workspace pages without either feature route-sniffing.
 *
 * Focusing itself is a ref hand-off, not a DOM probe on a fixed budget: the
 * pane's search `<input>` registers itself on mount via
 * `registerLibrarySearchInput`, and a pending request resolves the instant
 * that happens. That removes the two failure modes a probe had: a slow
 * mount (a MUI Drawer's slide, a cold render) outliving the probe window on
 * a loaded device, and depending on a `data-testid` a build plugin is free
 * to strip from a production bundle.
 */

type FocusHandler = () => void;

let handler: FocusHandler | null = null;

/** Returns an unregister function; a newer registration wins. */
export const registerLibrarySearchFocus = (h: FocusHandler): (() => void) => {
  handler = h;
  return () => {
    if (handler === h) {
      handler = null;
    }
  };
};

/** True when a workspace handled the focus request. */
export const requestLibrarySearchFocus = (): boolean => {
  if (!handler) {
    return false;
  }
  handler();
  return true;
};

let inputEl: HTMLInputElement | null = null;
let pending = false;
// The `select` half of a pending request. Held beside `pending` rather than
// folded into it: a bare boolean loses the caller's intent across a remount,
// and the mount then selects a query the caller explicitly asked to leave
// alone — the same erasure the mounted path guards against.
let pendingSelect = true;

/**
 * The pane's search input calls this from an `inputRef` callback, on every
 * mount and unmount. Mounting while a request is pending resolves it
 * immediately, so `focusLibrarySearchInput` never has to guess how long the
 * pane the handler just opened will take to render.
 */
export const registerLibrarySearchInput = (
  el: HTMLInputElement | null,
): void => {
  inputEl = el;
  if (inputEl && pending) {
    pending = false;
    inputEl.focus();
    if (pendingSelect) inputEl.select();
  } else if (!inputEl) {
    // A mount that would satisfy a pending request always consumes it
    // above before any unmount could fire, so a `null` call arriving while
    // `pending` is still true means the mount the request was waiting for
    // never came. Drop it here — otherwise it lingers and steals focus on
    // some later, unrelated mount of this input.
    pending = false;
  }
};

/**
 * Focus the pane's search input: immediately if it's already mounted,
 * otherwise once `registerLibrarySearchInput` reports the mount this
 * triggered. Idempotent while a request is pending — a second Cmd-K before
 * the first mount finishes does not queue a second focus-and-select, which
 * is what previously let it clobber text the user had already typed.
 *
 * `select` defaults true for Cmd-K, where replacing a stale query is the
 * point. A caller recovering focus *during* a search must pass false: the
 * selection would make the user's next keystroke replace what they had typed.
 * It is honoured on both paths — a request that has to wait for a mount
 * carries the choice with it.
 */
export const focusLibrarySearchInput = ({ select = true } = {}): void => {
  if (inputEl) {
    inputEl.focus();
    if (select) inputEl.select();
    return;
  }
  pending = true;
  pendingSelect = select;
};
